import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {
  MyHttpClientFactory,
  ResponseCasingEnum,
  environment,
} from '@ups/xplat/core';
import { CostDetailDto, IODataResult } from '@ups/xplat/api/dto';
import { ODataResult } from '@ups/xplat/features';

const GET_COST_DETAIL = '/api/cost-detail';

/**
 * Client for the in-app Cost Detail report (WI 30860). Talks to viewpoint-api's
 * job-scoped `GET /api/cost-detail` OData endpoint, the reliable UPS-owned
 * replacement for the flaky SSRS "VP Cost Detail Report". Job + date bounds ride
 * as plain query params (mirroring the report's parameters); the Kendo grid state
 * rides alongside as the OData query string so filter/sort/paging compose over the
 * raw query server-side.
 *
 * PascalCase response casing is required: OData grouping is field-name
 * case-sensitive server-side (same workaround as EquipmentVpService).
 */
@Injectable({
  providedIn: 'root',
})
export class CostDetailVpService {
  protected httpViewpointClient: HttpClient;

  constructor(clientFactory: MyHttpClientFactory) {
    this.httpViewpointClient = clientFactory.createHttpClient(
      environment.urls.viewpointAPI,
      true,
      ResponseCasingEnum.PascalCase
    );
  }

  /**
   * Queries cost-detail rows for a single job, optionally bounded by actual date.
   * A job is required (the report is job-scoped); without one an empty result is
   * returned rather than issuing an unbounded call.
   *
   * @param job the Viewpoint job id (required)
   * @param dateFrom optional inclusive lower bound, formatted `YYYY-MM-DD` (or null)
   * @param dateTo optional inclusive upper bound, formatted `YYYY-MM-DD` (or null)
   * @param oDataString the Kendo grid state as an OData query string (filter/sort/paging)
   */
  public queryCostDetail(
    job: string,
    dateFrom: string,
    dateTo: string,
    oDataString: string
  ): Observable<IODataResult<CostDetailDto>> {
    if (!job) {
      return of(ODataResult.EmptyODataResult);
    }

    let url = `${GET_COST_DETAIL}?$format=json&$count=true&job=${escape(job)}`;
    url += dateFrom ? `&dateFrom=${escape(dateFrom)}` : '';
    url += dateTo ? `&dateTo=${escape(dateTo)}` : '';
    url += oDataString ? `&${oDataString}` : '';

    return this.httpViewpointClient.get(url) as Observable<
      IODataResult<CostDetailDto>
    >;
  }
}
